/**
 * Messages API — Supabase PostgREST queries.
 */

import supabase from "../config/supabase";
import { USER_CARD_EMBED } from "./users";

const messagesApi = {
  async getNotificationConversation(messageId) {
    const {data: {user}} = await supabase.auth.getUser();
    if (!user) return {success: false, error: "Not authenticated"};
    const {data, error} = await supabase.from("messages")
      .select(`id, sender_id, content, sender:users!sender_id(${USER_CARD_EMBED})`)
      .eq("id", messageId).eq("receiver_id", user.id).maybeSingle();
    if (error || !data) return {success: false, error: "Message unavailable"};
    return {success: true, data: {id: data.sender_id, partnerId: data.sender_id,
      name: `${data.sender?.first_name || ""} ${data.sender?.last_name || ""}`.trim() || "Member",
      lastMessage: data.content, unread: 1}};
  },
  async listConversations(params = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    // Get distinct conversation partners
    const { data, error } = await supabase
      .from("messages")
      .select(
        `*, sender:users!sender_id(${USER_CARD_EMBED}), receiver:users!receiver_id(${USER_CARD_EMBED})`,
      )
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order("created_at", { ascending: false });

    if (error) return { success: false, error: error.message };

    // Group by conversation partner
    const convMap = new Map();
    for (const msg of data || []) {
      const partnerId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      if (!convMap.has(partnerId)) {
        const partner = msg.sender_id === user.id ? msg.receiver : msg.sender;
        convMap.set(partnerId, {
          partner_id: partnerId,
          partner,
          last_message: msg,
          unread_count: msg.receiver_id === user.id && !msg.is_read ? 1 : 0,
        });
      } else if (msg.receiver_id === user.id && !msg.is_read) {
        convMap.get(partnerId).unread_count += 1;
      }
    }

    return { success: true, data: Array.from(convMap.values()) };
  },

  async unreadCount() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, data: { count: 0 } };

    const { count, error } = await supabase
      .from("messages")
      .select("*", { count: "exact", head: true })
      .eq("receiver_id", user.id)
      .eq("is_read", false);

    if (error) return { success: false, error: error.message };
    return { success: true, data: { count: count || 0 } };
  },

  async getConversation(partnerId, params = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("messages")
      .select(
        `*, sender:users!sender_id(${USER_CARD_EMBED}), receiver:users!receiver_id(${USER_CARD_EMBED})`,
      )
      .or(
        `and(sender_id.eq.${user.id},receiver_id.eq.${partnerId}),and(sender_id.eq.${partnerId},receiver_id.eq.${user.id})`
      )
      .order("created_at", { ascending: true });

    if (error) return { success: false, error: error.message };

    // Mark received messages as read
    await supabase
      .from("messages")
      .update({ is_read: true })
      .eq("sender_id", partnerId)
      .eq("receiver_id", user.id)
      .eq("is_read", false);

    return { success: true, data: data || [] };
  },

  async send({ receiver_id, content, listing_id, listing_type }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("messages")
      .insert({
        sender_id: user.id,
        receiver_id,
        content,
        related_entity_id: listing_id || null,
        related_entity_type: listing_type || null,
        is_read: false,
      })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  async delete(messageId) {
    const { error } = await supabase.from("messages").delete().eq("id", messageId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default messagesApi;
